// The framed page's sign-in when its own Clerk says signed out: a session token from the
// extension that frames it.
//
// Measured on 2 Oct: under Clerk's development instance the frame's dev-browser state is
// partitioned, so `/embed/extension` always reports signed out even with a signed-in dashboard
// tab, while the extension's worker (synced from that tab) is signed in. So the frame asks its
// parent, the side panel, with `scout:token-request`; the panel asks the worker for a fresh
// token and posts back `scout:session-token`. The frame uses it as the Bearer for its API calls
// and asks again when it expires or the server answers 401.
//
// The rules that keep this safe:
// - a reply counts only from the pinned extension origin (`EXTENSION_ORIGINS`, derived from
//   `manifest.key`) *and* from `window.parent`, and only for a request id this page sent;
// - requests are posted to the extension origin by name, never '*';
// - the token lives in this object's memory only: never localStorage, never a log line.
//
// No DOM import, so `node --test` covers it with a fake window.

import { EXTENSION_ORIGINS, isTrustedExtensionOrigin } from './bridge.ts';
import type { BridgeEvent } from './bridge.ts';
import type { TokenOptions } from '../api/client.ts';

export const TOKEN_REQUEST_TYPE = 'scout:token-request';
export const TOKEN_REPLY_TYPE = 'scout:session-token';
// Longer than the worker's own per-read limit (10 s, `SESSION_READ_TIMEOUT_MS` in the
// extension's background.js), so a slow cold read answers rather than timing out here first.
// At 8 s, a panel opened while the worker was still loading Clerk gave up early and showed
// sign-in until something reloaded the frame (Vikram's test pass, 7 Oct: ~15 s of sign-in).
export const TOKEN_REQUEST_TIMEOUT_MS = 12000;
// No token on the first ask is not yet "signed out": a cold worker, or one busy with the
// overlay's read, often has it a moment later. Ask again after these pauses before saying so.
// The last pause outlasts the worker's 5 s signed-out memory, so it really asks Clerk again.
export const TOKEN_RETRY_DELAYS_MS = [1000, 2500, 5500];

/**
 * The first token, asking again after each of `delays` while there is none. Null only when
 * every ask came back empty: that, and only that, is "signed out".
 */
export async function firstTokenWithRetries(
  getToken: (options?: TokenOptions) => Promise<string | null>,
  wait: (milliseconds: number) => Promise<void>,
  delays: number[] = TOKEN_RETRY_DELAYS_MS,
): Promise<string | null> {
  let token = await getToken();
  for (const delay of delays) {
    if (token) return token;
    await wait(delay);
    token = await getToken({ skipCache: true });
  }
  return token;
}

// Signed out is not final. The strategist signs in on a dashboard tab *after* the panel said
// "signed out", and the frame must notice without a reload. It used to depend on the shell
// reloading it when Clerk's `__session` cookie was set (`sign_in_reload.js`). That is one shot
// on an event the panel does not control (clerk-js writes the cookie only while the dashboard
// document has focus or is the active context), and when it missed nothing else ever asked
// again: the panel stayed on "Open the dashboard to sign in" (8 Oct). So the frame asks itself: every
// 5 s for 5 minutes, and on focus or the Check again button after that. Each ask is one worker
// read at most, since the worker remembers signed out for 5 s.
export const SIGN_IN_POLL_INTERVAL_MS = 5000;
export const SIGN_IN_POLL_ATTEMPTS = 60;

/**
 * Asks for a token every `intervalMilliseconds`, up to `attempts` times, until one comes back.
 * Null when none did, or when `isCancelled()` turned true (the page moved on).
 */
export async function pollForToken(
  getToken: (options?: TokenOptions) => Promise<string | null>,
  wait: (milliseconds: number) => Promise<void>,
  isCancelled: () => boolean,
  intervalMilliseconds: number = SIGN_IN_POLL_INTERVAL_MS,
  attempts: number = SIGN_IN_POLL_ATTEMPTS,
): Promise<string | null> {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    await wait(intervalMilliseconds);
    if (isCancelled()) return null;
    const token = await getToken({ skipCache: true });
    if (isCancelled()) return null;
    if (token) return token;
  }
  return null;
}
// Clerk session tokens live about a minute; ask for the next one a little before this one dies.
export const TOKEN_REFRESH_MARGIN_SECONDS = 15;

/** The slice of `window` the token source uses. A real window fits it. */
export interface FrameHost {
  parent: { postMessage(message: unknown, targetOrigin: string): void };
  addEventListener(type: 'message', listener: (event: BridgeEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: BridgeEvent) => void): void;
}

/** The token in a reply to `requestId`, `null` for "signed out", or undefined when not a reply. */
export function tokenReplyFromEvent(event: BridgeEvent, parentWindow: unknown, requestId: string): string | null | undefined {
  if (!isTrustedExtensionOrigin(event.origin) || event.source !== parentWindow) return undefined;
  const data = event.data as { type?: unknown; request_id?: unknown; token?: unknown } | null;
  if (!data || typeof data !== 'object' || data.type !== TOKEN_REPLY_TYPE || data.request_id !== requestId) return undefined;
  return typeof data.token === 'string' && data.token.length > 0 ? data.token : null;
}

// The JWT's claims, unverified: only to know when to refresh and whose it is. The server verifies.
function tokenClaims(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as unknown;
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Seconds since the epoch when the token expires, or null when it carries no `exp`. */
export function tokenExpirySeconds(token: string): number | null {
  const expiry = tokenClaims(token)?.exp;
  return typeof expiry === 'number' ? expiry : null;
}

/** True while the token has more than the refresh margin left. No `exp` counts as stale. */
export function tokenIsFresh(token: string, nowMilliseconds: number): boolean {
  const expiry = tokenExpirySeconds(token);
  if (expiry === null) return false;
  return expiry - TOKEN_REFRESH_MARGIN_SECONDS > nowMilliseconds / 1000;
}

/** The email claim the server reads too (`access/auth.py`), for display only. */
export function emailFromToken(token: string): string | null {
  const claims = tokenClaims(token);
  if (!claims) return null;
  for (const name of ['email', 'primary_email_address', 'email_address']) {
    const value = claims[name];
    if (typeof value === 'string' && value.trim()) return value.trim().toLowerCase();
  }
  return null;
}

let requestCounter = 0;

function newRequestId(): string {
  requestCounter += 1;
  const random = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : String(Math.random()).slice(2);
  return `token-${requestCounter}-${random}`;
}

/**
 * The frame's token getter. A class because it holds genuine state: the current token and the
 * one request in flight, which concurrent API calls share.
 */
export class FrameTokenSource {
  private token: string | null = null;
  private pending: Promise<string | null> | null = null;
  private readonly host: FrameHost;
  private readonly now: () => number;
  private readonly timeoutMilliseconds: number;

  constructor(host: FrameHost, now: () => number = Date.now, timeoutMilliseconds = TOKEN_REQUEST_TIMEOUT_MS) {
    this.host = host;
    this.now = now;
    this.timeoutMilliseconds = timeoutMilliseconds;
  }

  /** A `TokenGetter`: the held token while fresh, else a new one from the extension. */
  getToken = (options?: TokenOptions): Promise<string | null> => {
    if (options?.skipCache) this.token = null;
    if (this.token && tokenIsFresh(this.token, this.now())) return Promise.resolve(this.token);
    if (!this.pending) this.pending = this.requestToken();
    return this.pending;
  };

  private requestToken(): Promise<string | null> {
    const requestId = newRequestId();
    const host = this.host;
    return new Promise<string | null>(resolve => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      const finish = (token: string | null) => {
        if (timer !== null) clearTimeout(timer);
        host.removeEventListener('message', onMessage);
        this.token = token;
        this.pending = null;
        resolve(token);
      };

      // Only the pinned extension, framing us, answering this request.
      const onMessage = (event: BridgeEvent) => {
        const token = tokenReplyFromEvent(event, host.parent, requestId);
        if (token === undefined) return;
        finish(token);
      };

      host.addEventListener('message', onMessage);
      timer = setTimeout(() => finish(null), this.timeoutMilliseconds);

      // To the extension origin by name: if anything else frames us, Chrome drops the request.
      for (const origin of EXTENSION_ORIGINS) {
        host.parent.postMessage({ type: TOKEN_REQUEST_TYPE, request_id: requestId }, origin);
      }
    });
  }
}
